<script setup>
import ArrowUUpLeftIcon from '@iconify-vue/ph/arrow-u-up-left';
import ClipboardTextIcon from '@iconify-vue/ph/clipboard-text';
import FolderPlusIcon from '@iconify-vue/ph/folder-plus';
import FolderIcon from '@iconify-vue/ph/folder-simple-fill';
import ImageIcon from '@iconify-vue/ph/image';
import TrashIcon from '@iconify-vue/ph/trash';
import UploadSimpleIcon from '@iconify-vue/ph/upload-simple';
import LoadingSpinner from '@js/components/LoadingSpinner.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DialogModal from '@js/components/dialogs/DialogModal.vue';
import { toastError, toastSuccess, toastWarning } from '@js/utils/toast';
import { computed, ref, watch } from 'vue';

const { path = '/', selectable = false } = defineProps({
    path: {
        type: String,
        required: false,
    },
    selectable: {
        type: Boolean,
        required: false,
    },
});

const emit = defineEmits(['select']);

const isOpen = defineModel({ default: false });

const isLoading = ref(false);

const isUploading = ref(false);

const explorerData = ref(null);

const currentPath = ref('/');

const files = ref(null);

const newFolderName = ref('');

const fileInputKey = ref(Date.now());

const isDragging = ref(false);

const selectedFile = ref(null);

const basePath = path ? '/' + path.replace(/^\/|\/$/g, '') : '';

function fullCurrentPath() {
    if (currentPath.value !== '/') {
        return currentPath.value;
    }

    return basePath || '/';
}

const breadcrumbs = computed(() => {
    if (currentPath.value === '/') return [];
    const baseSegments = basePath.split('/').filter(Boolean);
    const allSegments = currentPath.value.split('/').filter(Boolean);
    const extraSegments = allSegments.slice(baseSegments.length);
    return extraSegments.map((segment, index) => ({
        name: segment,
        path: basePath + '/' + extraSegments.slice(0, index + 1).join('/'),
    }));
});

watch(isOpen, (value) => {
    if (value) {
        currentPath.value = '/';
        selectedFile.value = null;
        refreshGallery();
    } else {
        explorerData.value = null;
        files.value = null;
        newFolderName.value = '';
        selectedFile.value = null;
    }
});

function refreshGallery() {
    isLoading.value = true;

    axios
        .get(
            route('front.media.getFilesAndDirectories', {
                path: fullCurrentPath(),
            }),
        )
        .then((response) => {
            explorerData.value = {
                folders: response?.data?.directories,
                files: response?.data?.files,
            };
        })
        .catch((error) => {
            toastError(error);
        })
        .finally(() => {
            isLoading.value = false;
        });
}

function createFolder() {
    if (!newFolderName.value) return;

    axios
        .post(route('front.media.postCreateDirectory'), {
            path: fullCurrentPath(),
            name: newFolderName.value,
        })
        .then(() => {
            toastSuccess('Dossier créé');
            newFolderName.value = '';
        })
        .catch((error) => {
            toastError(error);
        })
        .finally(refreshGallery);
}

function uploadFiles() {
    if (files.value && files.value.length > 0) {
        isUploading.value = true;
        let formData = new FormData();
        for (let i = 0; i < files.value.length; i++) {
            formData.append('files[]', files.value[i]);
        }
        formData.append('path', fullCurrentPath());

        axios
            .post(route('front.media.postUploadFiles'), formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            })
            .then(() => {
                toastSuccess('Fichiers envoyés');
            })
            .catch((error) => {
                toastError(error);
            })
            .finally(() => {
                files.value = null;
                fileInputKey.value = Date.now();
                isUploading.value = false;
                refreshGallery();
            });
    } else {
        toastWarning('Aucun fichier sélectionné');
    }
}

function deleteFile(filePath) {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce fichier ?')) return;

    axios
        .post(route('front.media.deleteFile'), {
            path: filePath,
        })
        .then(() => {
            toastSuccess('Fichier supprimé');
        })
        .catch((error) => {
            toastError(error);
        })
        .finally(refreshGallery);
}

function deleteFolder(folderPath) {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce dossier ?')) return;

    axios
        .post(route('front.media.deleteDirectory'), {
            path: folderPath,
        })
        .then(() => {
            toastSuccess('Dossier supprimé');
        })
        .catch((error) => {
            toastError(error);
        })
        .finally(refreshGallery);
}

function navigatePath(navPath) {
    currentPath.value = navPath;
    refreshGallery();
}

function copyUrl(url) {
    navigator.clipboard.writeText(url).then(() => {
        toastSuccess('URL copiée');
    });
}

function onDragOver(event) {
    event.preventDefault();
    isDragging.value = true;
}

function onDragLeave() {
    isDragging.value = false;
}

function onDrop(event) {
    event.preventDefault();
    isDragging.value = false;
    const droppedFiles = event.dataTransfer?.files;
    if (droppedFiles?.length) {
        files.value = droppedFiles;
        uploadFiles();
    }
}

function onFileInputChange(event) {
    files.value = event.target.files;
}

function selectFile(file) {
    selectedFile.value = selectedFile.value === file ? null : file;
}

function confirmSelection() {
    if (selectedFile.value) {
        emit('select', selectedFile.value);
        isOpen.value = false;
    }
}
</script>

<template>
    <DialogModal v-model="isOpen" size="large">
        <template #trigger>
            <slot name="trigger" />
        </template>

        <template #title> Gérer les médias </template>

        <template #full-content>
            <div class="flex h-[70vh] divide-x divide-gray-200">
                <!-- Folders panel -->
                <div
                    class="flex w-64 shrink-0 flex-col overflow-y-auto bg-gray-50"
                >
                    <div class="p-4 pb-2">
                        <span
                            class="text-xs font-semibold tracking-wide text-gray-500 uppercase"
                        >
                            Dossiers
                        </span>
                    </div>

                    <div
                        v-if="isLoading"
                        class="flex flex-1 items-center justify-center"
                    >
                        <LoadingSpinner />
                    </div>

                    <div v-else class="flex-1 space-y-px px-2">
                        <div
                            v-if="currentPath !== '/'"
                            class="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-gray-600 transition hover:bg-gray-200/70"
                            @click="
                                navigatePath(
                                    (() => {
                                        const parent =
                                            currentPath
                                                .split('/')
                                                .slice(0, -1)
                                                .join('/') || '/';
                                        return parent === basePath ||
                                            parent.length <= basePath.length
                                            ? '/'
                                            : parent;
                                    })(),
                                )
                            "
                        >
                            <ArrowUUpLeftIcon class="size-4 shrink-0" />
                            <span>..</span>
                        </div>

                        <div
                            v-for="folder in explorerData?.folders"
                            :key="folder.name"
                            class="group flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm transition hover:bg-gray-200/70"
                            @click="navigatePath(folder.path)"
                        >
                            <FolderIcon
                                class="size-4 shrink-0 text-yellow-500"
                            />
                            <span class="grow truncate">{{ folder.name }}</span>
                            <TrashIcon
                                class="size-3.5 shrink-0 cursor-pointer text-red-400 opacity-0 transition group-hover:opacity-100 hover:text-red-600"
                                @click.stop="deleteFolder(folder.path)"
                            />
                        </div>
                    </div>

                    <div class="border-t border-gray-200 p-3">
                        <div class="flex items-center gap-1.5">
                            <FolderPlusIcon
                                class="size-4 shrink-0 text-gray-400"
                            />
                            <input
                                v-model="newFolderName"
                                class="min-w-0 flex-1 border-b border-gray-300 bg-transparent text-sm outline-none focus:border-indigo-500"
                                type="text"
                                placeholder="Nouveau dossier"
                                @keyup.enter="createFolder"
                            />
                            <button
                                class="shrink-0 cursor-pointer rounded-sm bg-indigo-600 px-2 py-0.5 text-xs text-white transition hover:bg-indigo-950"
                                @click="createFolder"
                            >
                                Créer
                            </button>
                        </div>
                    </div>
                </div>

                <div class="flex flex-1 flex-col overflow-hidden">
                    <div
                        class="flex items-center gap-1 border-b border-gray-200 bg-gray-50/50 px-4 py-2 text-sm"
                    >
                        <button
                            class="cursor-pointer text-gray-500 transition hover:text-indigo-600"
                            :class="{
                                'font-medium text-gray-800':
                                    currentPath === '/',
                            }"
                            @click="navigatePath('/')"
                        >
                            {{ path }}
                        </button>
                        <template
                            v-for="crumb in breadcrumbs"
                            :key="crumb.path"
                        >
                            <span class="text-gray-300">/</span>
                            <button
                                class="cursor-pointer truncate text-gray-500 transition hover:text-indigo-600"
                                :class="{
                                    'font-medium text-gray-800':
                                        crumb.path === currentPath,
                                }"
                                @click="navigatePath(crumb.path)"
                            >
                                {{ crumb.name }}
                            </button>
                        </template>
                    </div>

                    <div class="flex-1 overflow-y-auto p-4">
                        <div
                            v-if="isLoading"
                            class="flex h-full items-center justify-center"
                        >
                            <LoadingSpinner />
                        </div>

                        <div
                            v-else-if="explorerData?.files?.length"
                            class="grid grid-cols-4 gap-3"
                        >
                            <div
                                v-for="file in explorerData?.files"
                                :key="file.name"
                                class="group relative overflow-hidden rounded-lg border bg-white shadow-xs transition hover:shadow-md"
                                :class="
                                    selectable && selectedFile === file
                                        ? 'border-indigo-500 ring-2 ring-indigo-300'
                                        : 'border-gray-200 hover:border-indigo-300'
                                "
                                @click="selectable ? selectFile(file) : null"
                            >
                                <div
                                    class="aspect-square overflow-hidden"
                                    :class="{ 'cursor-pointer': selectable }"
                                >
                                    <img
                                        :src="file.url"
                                        :alt="file.name"
                                        class="size-full object-cover transition duration-200 group-hover:scale-105"
                                    />
                                </div>

                                <div
                                    class="truncate border-t border-gray-100 px-2 py-1.5 text-center text-xs text-gray-600"
                                    :title="file.name"
                                >
                                    {{ file.name }}
                                </div>

                                <div
                                    class="absolute inset-0 flex items-center justify-center gap-2 bg-black/40 opacity-0 transition duration-200 group-hover:opacity-100"
                                >
                                    <button
                                        class="rounded-full bg-white p-2 shadow-md transition hover:bg-gray-100"
                                        title="Copier l'URL"
                                        @click.stop="copyUrl(file.url)"
                                    >
                                        <ClipboardTextIcon
                                            class="size-4 text-indigo-600"
                                        />
                                    </button>
                                    <button
                                        class="rounded-full bg-white p-2 shadow-md transition hover:bg-gray-100"
                                        title="Supprimer"
                                        @click.stop="deleteFile(file.path)"
                                    >
                                        <TrashIcon
                                            class="size-4 text-red-500"
                                        />
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div
                            v-else
                            class="flex h-full flex-col items-center justify-center gap-3 text-gray-400"
                        >
                            <ImageIcon class="size-16 text-gray-300" />
                            <span class="text-sm font-medium">
                                Aucun fichier dans ce dossier
                            </span>
                        </div>
                    </div>

                    <div
                        v-if="selectable && selectedFile"
                        class="flex items-center gap-3 border-t border-gray-200 bg-indigo-50 px-4 py-3"
                    >
                        <img
                            :src="selectedFile.url"
                            :alt="selectedFile.name"
                            class="size-10 rounded border border-indigo-200 object-cover"
                        />
                        <span
                            class="flex-1 truncate text-sm font-medium text-gray-700"
                        >
                            {{ selectedFile.name }}
                        </span>
                        <ActionButton :callback="confirmSelection" size="small">
                            Insérer l'image
                        </ActionButton>
                    </div>

                    <div class="border-t border-gray-200 p-4">
                        <div
                            class="flex items-center gap-3 rounded-lg border-2 border-dashed px-4 py-3 transition"
                            :class="
                                isDragging
                                    ? 'border-indigo-400 bg-indigo-50'
                                    : 'border-gray-300 bg-gray-50'
                            "
                            @dragover="onDragOver"
                            @dragleave="onDragLeave"
                            @drop="onDrop"
                        >
                            <UploadSimpleIcon
                                class="size-5 shrink-0"
                                :class="
                                    isDragging
                                        ? 'text-indigo-500'
                                        : 'text-gray-400'
                                "
                            />

                            <div class="flex-1 text-sm text-gray-500">
                                <template v-if="isDragging">
                                    Déposer les fichiers ici...
                                </template>
                                <template v-else-if="files && files.length > 0">
                                    {{ files.length }} fichier(s) sélectionné(s)
                                </template>
                                <template v-else>
                                    Glisser-déposer ou
                                    <label
                                        class="cursor-pointer font-medium text-indigo-600 hover:text-indigo-800"
                                    >
                                        parcourir
                                        <input
                                            :key="fileInputKey"
                                            type="file"
                                            multiple
                                            accept="image/png, image/jpeg, image/jpg, image/gif, image/webp"
                                            class="hidden"
                                            @change="onFileInputChange"
                                        />
                                    </label>
                                </template>
                            </div>

                            <ActionButton
                                v-if="files && files.length > 0"
                                :callback="uploadFiles"
                                :loading="isUploading"
                                size="small"
                                :left-icon="UploadSimpleIcon"
                            >
                                Envoyer
                            </ActionButton>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </DialogModal>
</template>
